// src/pages/vendor/VendorOnboarding.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";

function formatMoney(v) {
  return `${Number(v || 0).toLocaleString("fr-FR")} FCFA`;
}

export default function VendorOnboarding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (!token) {
        setError("Lien d’onboarding invalide.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const fn = httpsCallable(functions, "getVendorOnboardingInfo");
        const res = await fn({ token });

        if (!mounted) return;
        setData(res.data);
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setError(
          err?.message || "Impossible de charger les informations d’onboarding."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [token]);

  const canSetPassword = useMemo(() => {
    if (!data) return false;
    if (!data.requiresPayment) return true;
    return data.paymentStatus === "paid";
  }, [data]);

  const paymentPending = data?.requiresPayment && data?.paymentStatus !== "paid";

  const handleFakeContinueToPayment = async () => {
    try {
      setPaying(true);

      // Ici tu remplaceras par ta vraie page / passerelle de paiement
      // Ex: navigate(`/vendor/pay-pack?token=${encodeURIComponent(token)}`)
      navigate(`/vendor/pay-pack?token=${encodeURIComponent(token)}`);
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="vo-page">
        <div className="vo-card">
          <p>Chargement de votre parcours vendeur...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="vo-page">
        <div className="vo-card">
          <h1 className="vo-title">Lien invalide ou expiré</h1>
          <p className="vo-muted">
            {error || "Ce lien n’est plus utilisable."}
          </p>
          <Link to="/" className="vo-btn vo-btn-primary">
            Retour à l’accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="vo-page">
      <div className="vo-shell">
        <div className="vo-hero">
          <div>
            <span className="vo-kicker">Onboarding vendeur</span>
            <h1 className="vo-title">Bienvenue {data.shopName || "sur GoShop"}</h1>
            <p className="vo-muted">
              Votre compte vendeur a été préparé. Il vous reste à terminer les
              étapes ci-dessous pour activer complètement votre accès.
            </p>
          </div>

          <div className="vo-badgeCard">
            <div className="vo-badge">Pack</div>
            <strong>{data.packName || data.packId}</strong>
            <p>{formatMoney(data.totalFee)}</p>
          </div>
        </div>

        <div className="vo-grid">
          <section className="vo-card">
            <h2 className="vo-sectionTitle">Résumé de votre activation</h2>

            <div className="vo-row">
              <span>Email</span>
              <strong>{data.email || "—"}</strong>
            </div>

            <div className="vo-row">
              <span>Pack</span>
              <strong>{data.packName || data.packId || "—"}</strong>
            </div>

            <div className="vo-row">
              <span>Montant</span>
              <strong>{formatMoney(data.totalFee)}</strong>
            </div>

            <div className="vo-row">
              <span>Paiement</span>
              <strong>
                {data.paymentStatus === "paid"
                  ? "Payé"
                  : data.paymentStatus === "not_required"
                  ? "Non requis"
                  : "En attente"}
              </strong>
            </div>

            <div className="vo-row">
              <span>Mot de passe</span>
              <strong>
                {data.passwordSetupStatus === "completed"
                  ? "Déjà défini"
                  : "À définir"}
              </strong>
            </div>
          </section>

          <section className="vo-card">
            <h2 className="vo-sectionTitle">Étapes à suivre</h2>

            <div className="vo-step">
              <div className={`vo-stepDot ${paymentPending ? "pending" : "done"}`} />
              <div>
                <strong>1. Paiement du pack</strong>
                <p className="vo-muted">
                  {data.requiresPayment
                    ? data.paymentStatus === "paid"
                      ? "Votre paiement a été confirmé."
                      : "Votre pack doit être payé avant l’activation complète."
                    : "Aucun paiement n’est requis pour ce pack."}
                </p>
              </div>
            </div>

            <div className="vo-step">
              <div className={`vo-stepDot ${canSetPassword ? "active" : "locked"}`} />
              <div>
                <strong>2. Définition du mot de passe</strong>
                <p className="vo-muted">
                  {canSetPassword
                    ? "Vous pouvez maintenant définir votre mot de passe GoShop."
                    : "Cette étape sera disponible après confirmation du paiement."}
                </p>
              </div>
            </div>

            <div className="vo-actions">
              {paymentPending ? (
                <button
                  type="button"
                  className="vo-btn vo-btn-primary"
                  onClick={handleFakeContinueToPayment}
                  disabled={paying}
                >
                  {paying ? "Redirection..." : "Payer mon pack"}
                </button>
              ) : null}

              {canSetPassword ? (
                <Link
                  to={`/vendor/set-password?token=${encodeURIComponent(token)}`}
                  className="vo-btn vo-btn-dark"
                >
                  Définir mon mot de passe
                </Link>
              ) : null}
            </div>
          </section>
        </div>
      </div>

      <style>{`
        .vo-page{
          min-height:100vh;
          background:linear-gradient(180deg,#f8fafc 0%,#eef2ff 100%);
          padding:24px 16px;
        }
        .vo-shell{max-width:1000px;margin:0 auto;display:flex;flex-direction:column;gap:16px;}
        .vo-hero{
          background:#fff;
          border:1px solid #e5e7eb;
          border-radius:24px;
          padding:20px;
          display:flex;
          justify-content:space-between;
          gap:16px;
          flex-wrap:wrap;
          box-shadow:0 18px 40px rgba(15,23,42,.08);
        }
        .vo-kicker{
          display:inline-flex;padding:4px 10px;border-radius:999px;background:#ede9fe;
          color:#5b21b6;font-size:11px;font-weight:900;margin-bottom:8px;
        }
        .vo-title{margin:0 0 6px;font-size:28px;font-weight:900;color:#0f172a;}
        .vo-muted{margin:0;color:#64748b;font-size:14px;}
        .vo-badgeCard{
          min-width:240px;background:#fff7ed;border:1px solid #fed7aa;border-radius:18px;padding:14px;
        }
        .vo-badge{
          display:inline-flex;padding:3px 8px;border-radius:999px;background:#ffedd5;color:#9a3412;
          font-size:11px;font-weight:900;margin-bottom:6px;
        }
        .vo-grid{
          display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px;
        }
        .vo-card{
          background:#fff;border:1px solid #e5e7eb;border-radius:22px;padding:18px;
          box-shadow:0 14px 32px rgba(15,23,42,.06);
        }
        .vo-sectionTitle{margin:0 0 14px;font-size:18px;font-weight:900;color:#0f172a;}
        .vo-row{
          display:flex;justify-content:space-between;gap:16px;padding:10px 0;border-bottom:1px solid #f1f5f9;
        }
        .vo-step{display:flex;gap:12px;align-items:flex-start;margin-bottom:14px;}
        .vo-stepDot{
          width:14px;height:14px;border-radius:999px;margin-top:4px;flex-shrink:0;background:#cbd5e1;
        }
        .vo-stepDot.done{background:#16a34a;}
        .vo-stepDot.active{background:#2563eb;}
        .vo-stepDot.locked{background:#94a3b8;}
        .vo-stepDot.pending{background:#f59e0b;}
        .vo-actions{display:flex;gap:10px;flex-wrap:wrap;margin-top:14px;}
        .vo-btn{
          display:inline-flex;align-items:center;justify-content:center;text-decoration:none;
          padding:10px 16px;border-radius:999px;font-size:14px;font-weight:800;border:none;cursor:pointer;
        }
        .vo-btn-primary{background:#16a34a;color:#fff;}
        .vo-btn-dark{background:#0f172a;color:#fff;}
        @media(max-width:760px){
          .vo-grid{grid-template-columns:1fr;}
          .vo-title{font-size:24px;}
        }
      `}</style>
    </div>
  );
}